/* eslint-disable no-magic-numbers */
import { useEffect, useRef, useState } from "react";
import { TimeCodeItem } from "./TimeCodeItem";
import { positionToMs } from "./helpers";
import { clsx } from "clsx";

export interface IProgressBarProps {
  max: number;
  currentTime: number;
  onChange: (time: number) => void;
  onChangeEnd?: () => void;
  fullScreen?: boolean;
}

export const ProgressBar: React.FC<IProgressBarProps> = ({
  max = 1000,
  currentTime = 0,
  onChange = () => undefined,
  onChangeEnd = () => undefined,
  fullScreen
}) => {
  const [seekHoverPosition, setSeekHoverTime] = useState(0);
  const [trackWidth, setTrackWidth] = useState(0);
  const [isEnlarged, setIsEnlarged] = useState(false);

  const seeking = useRef(false);
  const mobileSeeking = useRef(false);
  const trackElement = useRef<HTMLDivElement>(null);

  const hoverTimeValue = positionToMs(max, seekHoverPosition, trackWidth);

  const changeCurrentTimePosition = (pageX: number): void => {
    const left = trackElement.current?.getBoundingClientRect().left || 0;
    let position = pageX - left;

    position = position < 0 ? 0 : position;
    position = position > trackWidth ? trackWidth : position;

    setSeekHoverTime(position);

    const percent = (position * 100) / trackWidth;
    const time = +(percent * (max / 100)).toFixed(0);

    onChange(time);
  };

  const handleTouchSeeking = (event: TouchEvent): void => {
    event.preventDefault();
    event.stopPropagation();

    if (!mobileSeeking.current) {
      return;
    }

    const { changedTouches } = event;

    let pageX = changedTouches?.[changedTouches.length - 1]?.pageX || 0;

    pageX = pageX < 0 ? 0 : pageX;

    // setIsEnlarged(true);
    changeCurrentTimePosition(pageX);
  };

  const handleSeeking = (event: MouseEvent): void => {
    if (seeking.current) {
      changeCurrentTimePosition(event.pageX);
    }
  };

  const setTrackWidthState = (): void => {
    if (trackElement.current) {
      setTrackWidth(trackElement.current.offsetWidth);
    }
  };

  const handleTrackHover = (
    clear: boolean,
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void => {
    const left = trackElement.current?.getBoundingClientRect().left || 0;
    const position = clear ? 0 : event.pageX - left;

    setSeekHoverTime(position);
  };

  const getThumbHandlerPosition = (): { transform: string } => {
    const position = trackWidth / (max / currentTime);

    return { transform: `translateX(${position}px)` };
  };

  const setMobileSeeking = (state = true): void => {
    mobileSeeking.current = state;
    setSeekHoverTime(state ? seekHoverPosition : 0);
  };

  const setSeeking = (state: boolean, event: MouseEvent): void => {
    event.preventDefault();

    seeking.current = state;
    handleSeeking(event);

    setSeekHoverTime(state ? seekHoverPosition : 0);
  };

  const mouseSeekingHandler = (event: MouseEvent): void => {
    setSeeking(false, event);
    onChangeEnd();
  };

  const mobileTouchSeekingHandler = (): void => {
    setMobileSeeking(false);
    onChangeEnd();
  };

  const onMobileTouchStart = () => {
    setIsEnlarged(true);
    setMobileSeeking(true);
  };

  const onMobileTouchEnd = () => {
    setTimeout(() => setIsEnlarged(false), 2000);
  };

  useEffect(() => {
    setTrackWidthState();

    window.addEventListener("resize", setTrackWidthState);
    window.addEventListener("mousemove", handleSeeking);
    window.addEventListener("mouseup", mouseSeekingHandler);
    window.addEventListener("touchmove", handleTouchSeeking);
    window.addEventListener("touchend", mobileTouchSeekingHandler);

    return () => {
      window.removeEventListener("resize", setTrackWidthState);
      window.removeEventListener("mousemove", handleSeeking);
      window.removeEventListener("mouseup", mouseSeekingHandler);
      window.removeEventListener("touchmove", handleTouchSeeking);
      window.removeEventListener("touchend", mobileTouchSeekingHandler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [max, trackWidth]);

  useEffect(() => {
    setTimeout(() => setTrackWidthState(), 1);
  }, [fullScreen]);

  return (
    <div className={clsx("seek-slider", isEnlarged && "enlarged")}>
      <div
        className="track"
        ref={trackElement}
        onMouseMove={(event) => handleTrackHover(false, event)}
        onMouseLeave={(event) => handleTrackHover(true, event)}
        onMouseDown={(event) => setSeeking(true, event.nativeEvent)}
        onTouchStart={onMobileTouchStart}
        onTouchEnd={onMobileTouchEnd}
      >
        <TimeCodeItem
          trackWidth={trackWidth}
          maxTime={max}
          startTime={0}
          endTime={max}
          currentTime={currentTime}
          seekHoverTime={hoverTimeValue}
        />
      </div>

      <div
        className="thumb"
        style={getThumbHandlerPosition()}
      >
        <div className="handler" />
      </div>
    </div>
  );
};
